import { usePostHog } from 'posthog-js/react';
import { useAuth } from '../contexts/AuthContext';
import { getCurrentReferralData, clearReferralData } from '../utils/adReferralTracking';

export const usePostHogTracking = () => {
  const posthog = usePostHog();
  const { user, isAuthenticated } = useAuth();

  const trackEvent = (eventName, properties = {}) => {
    try {
      if (!posthog) return;

      // Add user context to all events if authenticated
      const enhancedProperties = {
        ...properties,
        app_surface: 'website',
        ...(isAuthenticated && user && {
          user_id: user.id,
          user_email: user.email,
          is_authenticated: true
        }),
        timestamp: new Date().toISOString()
      };

      posthog.capture(eventName, enhancedProperties);
    } catch (error) {
      console.warn('PostHog tracking failed:', error);
    }
  };

  const trackPageView = (pageName, additionalProperties = {}) => {
    trackEvent('page_viewed', {
      page_name: pageName,
      ...additionalProperties
    });
  };

  const trackNotificationAction = (action, notificationData = {}) => {
    trackEvent('notification_action', {
      action,
      ...notificationData
    });
  };

  const trackFishInteraction = (action, fishData = {}) => {
    trackEvent('fish_interaction', {
      action,
      ...fishData
    });
  };

  const trackFilterUsage = (filterType, filterValue) => {
    trackEvent('filter_used', {
      filter_type: filterType,
      filter_value: filterValue
    });
  };

  const trackReviewAction = (action, reviewData = {}) => {
    trackEvent('review_action', {
      action,
      ...reviewData
    });
  };

  const trackClassifiedAction = (action, classifiedData = {}) => {
    trackEvent('classified_action', {
      action,
      ...classifiedData
    });
  };

  const trackNewsletterAction = (action, subscriptionData = {}) => {
    trackEvent('newsletter_action', {
      action,
      ...subscriptionData
    });
  };

  const trackFavoritesAction = (action, favoritesData = {}) => {
    trackEvent('favorites_action', {
      action,
      ...favoritesData
    });
  };

  const trackAdReferral = (action, referralData = {}) => {
    trackEvent('ad_referral_action', {
      action,
      ...referralData
    });
  };

  const trackReefWizardAction = (action, wizardData = {}) => {
    trackEvent('reef_wizard_action', {
      action,
      ...wizardData
    });
  };

  const trackShoppingAssistantAction = (action, data = {}) => {
    trackEvent('shopping_assistant_action', {
      action,
      ...data
    });
  };

  const getReferralData = () => {
    return getCurrentReferralData();
  };

  const clearSessionReferralData = () => {
    clearReferralData();
    // Also unregister from PostHog if possible
    if (posthog) {
      try {
        // Note: PostHog doesn't have an "unregister" method, 
        // so we can't remove session properties once set
        console.log('Referral data cleared from session storage');
      } catch (error) {
        console.warn('Failed to clear PostHog referral data:', error);
      }
    }
  };

  return {
    trackEvent,
    trackPageView,
    trackNotificationAction,
    trackFishInteraction,
    trackFilterUsage,
    trackReviewAction,
    trackClassifiedAction,
    trackFavoritesAction,
    trackNewsletterAction,
    trackAdReferral,
    trackReefWizardAction,
    trackShoppingAssistantAction,
    getReferralData,
    clearSessionReferralData
  };
};
